// Per-page SEO head for the static (client-rendered) deployment: fetches the same meta the server renderer
// would inject (/api/seo/meta) and applies title, description, canonical, hreflang, robots, Open Graph,
// Twitter and schema.org JSON-LD on every route change. Nodes it adds carry data-seo so they can be replaced.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/lib/api";

const ALLOWED = new Set(["TITLE", "META", "LINK", "SCRIPT"]);
const SKIP = /^\/(app|print|onboarding|invite|verify-email|auth)(\/|$)/;

export function applyHead(headHtml) {
  const tpl = document.createElement("template");
  tpl.innerHTML = headHtml || "";
  document.head.querySelectorAll("[data-seo]").forEach((n) => n.remove());
  // a 404 page is showing (useNoindex): keep its "Not found" title and robots tag; the meta fetched for the path is the
  // fallback (home) head, whose title, canonical and robots would be wrong here
  const notFound = !!document.head.querySelector("meta[data-404]");
  for (const n of Array.from(tpl.content.children)) {
    if (!ALLOWED.has(n.tagName)) continue;
    if (notFound && (n.tagName === "TITLE" || (n.tagName === "LINK" && n.getAttribute("rel") === "canonical") || (n.tagName === "META" && n.getAttribute("name") === "robots"))) continue;
    if (n.tagName === "SCRIPT" && n.getAttribute("type") !== "application/ld+json") continue; // data only, never executable
    if (n.tagName === "TITLE") { document.title = n.textContent; continue; }
    if (n.tagName === "META" && n.getAttribute("name") === "description") document.head.querySelectorAll('meta[name="description"]:not([data-seo])').forEach((x) => x.remove());
    const el = document.createElement(n.tagName.toLowerCase());
    for (const a of Array.from(n.attributes)) el.setAttribute(a.name, a.value);
    if (n.tagName === "SCRIPT") el.textContent = n.textContent;
    el.setAttribute("data-seo", "");
    document.head.appendChild(el);
  }
}

export function useSeoHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (SKIP.test(pathname)) return undefined;
    const ctl = new AbortController();
    fetch(`${API}/seo/meta?path=${encodeURIComponent(pathname)}&host=${encodeURIComponent(window.location.host)}`, { signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (m && m.head) applyHead(m.head); })
      .catch(() => {});
    return () => ctl.abort();
  }, [pathname]);
}

// 404 pages: the static host answers 200 for every path, so tell crawlers not to index the page (a soft-404 signal).
export function useNoindex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, follow"; m.setAttribute("data-404", "1");
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
}
